import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase, readableError } from '../lib/supabase'
import { useSession } from '../lib/session'
import { Shell } from '../components/Shell'
import { Sill, Spinner } from '../components/ui'

const REQUEST_KINDS = [
  { id: 'publishing_registration', label: 'Publishing registration',
    hint: 'Beseter/SPM registers this song for you as administering publisher' },
  { id: 'distribution', label: 'Distribution assistance',
    hint: 'Get help getting a recording out to streaming platforms' },
]

/**
 * One hashed, versioned record per song, generated server-side whenever the
 * song is finished or sent (generate_publisher_document, called from
 * finish_song/submit_song) — never computed in the browser, so the hash can't
 * be produced without matching what's actually on file. This is what a
 * publisher can be shown, not a rendering of the song itself.
 */
export default function Catalog() {
  const { user, isPlus } = useSession()
  const nav = useNavigate()
  const [songs, setSongs] = useState(null)
  const [requests, setRequests] = useState([])
  const [open, setOpen] = useState(null)
  const [copied, setCopied] = useState(null)
  const [requesting, setRequesting] = useState(null)
  const [err, setErr] = useState(null)

  const loadRequests = useCallback(async () => {
    const { data } = await supabase.from('song_publishing_requests')
      .select('id, song_id, kind, status').eq('user_id', user.id)
    setRequests(data ?? [])
  }, [user])

  useEffect(() => {
    (async () => {
      const { data, error } = await supabase.from('songs')
        .select(`id, title, song_number, finished_at,
                 song_publisher_documents(id, version, hash, generated_reason, generated_at, snapshot)`)
        .eq('songwriter_id', user.id)
        .not('finished_at', 'is', null)
        .order('finished_at', { ascending: false })
      if (error) return setErr(readableError(error))
      setSongs((data ?? []).filter((s) => s.song_publisher_documents?.length))
      await loadRequests()
    })()
  }, [user, loadRequests])

  const requestFor = (songId, kind) => requests.find((r) => r.song_id === songId && r.kind === kind)

  const submitRequest = async (songId, kind) => {
    if (!isPlus) { nav('/plans'); return }
    setRequesting(`${songId}:${kind}`)
    const { error } = await supabase.from('song_publishing_requests')
      .insert({ song_id: songId, user_id: user.id, kind })
    setRequesting(null)
    if (error) return setErr(readableError(error))
    await loadRequests()
  }

  const copyHash = async (hash) => {
    await navigator.clipboard.writeText(hash)
    setCopied(hash)
    setTimeout(() => setCopied(null), 1500)
  }

  const downloadSnapshot = (song, doc) => {
    const blob = new Blob([JSON.stringify(doc.snapshot, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${song.song_number || song.id}-v${doc.version}.json`
    a.click()
    URL.revokeObjectURL(url)
  }

  if (!songs) return <Shell tabs><Spinner /></Shell>

  return (
    <Shell tabs>
      <button className="backlink" onClick={() => nav('/account')}>‹ Account</button>
      <h2 className="q">My Catalog</h2>
      <p className="sub">
        A hashed, versioned record of each finished song — what a publisher can
        be shown to verify the splits, signatures and rights on file.
      </p>
      <Sill />

      {songs.length === 0 && (
        <p className="small">Nothing here yet — this fills in once a song is finished.</p>
      )}

      {songs.map((s) => {
        const docs = [...s.song_publisher_documents].sort((a, b) => b.version - a.version)
        const isOpen = open === s.id
        return (
          <div key={s.id} className="tile" style={{ marginTop: 10 }}>
            <button className="action col" style={{ padding: 0 }}
                    onClick={() => setOpen(isOpen ? null : s.id)}>
              <div className="row">
                <span>{s.title || 'Untitled'}</span>
                <span className="pill">{docs.length} version{docs.length === 1 ? '' : 's'}</span>
              </div>
              <small>{s.song_number || 'No reference yet'}</small>
            </button>

            {isOpen && (
              <div style={{ marginTop: 10 }}>
                {docs.map((d) => (
                  <div key={d.id} className="songrow">
                    <div className="row">
                      <span>Version {d.version}</span>
                      <span className="pill">{d.generated_reason}</span>
                    </div>
                    <small>{new Date(d.generated_at).toLocaleString()}</small>
                    <code className="small" style={{ display: 'block', wordBreak: 'break-all', marginTop: 4 }}>
                      {d.hash}
                    </code>
                    <div className="songtools">
                      <button className="iconbtn" onClick={() => copyHash(d.hash)}>
                        {copied === d.hash ? 'Copied' : 'Copy hash'}
                      </button>
                      <button className="iconbtn" onClick={() => downloadSnapshot(s, d)}>
                        Download JSON
                      </button>
                    </div>
                  </div>
                ))}

                <div className="eyebrow" style={{ marginTop: 14 }}>Publishing & distribution</div>
                {REQUEST_KINDS.map((k) => {
                  const req = requestFor(s.id, k.id)
                  const busy = requesting === `${s.id}:${k.id}`
                  return (
                    <div key={k.id} className="songrow">
                      <div className="row">
                        <span>{k.label}{!isPlus && ' · Plus'}</span>
                        {req && <span className="pill">{req.status.replace('_', ' ')}</span>}
                      </div>
                      <small>{k.hint}</small>
                      {!req && (
                        <button className="iconbtn" disabled={busy} aria-busy={busy}
                                onClick={() => submitRequest(s.id, k.id)} style={{ marginTop: 6 }}>
                          {isPlus ? 'Request' : 'Upgrade to request'}
                        </button>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )
      })}

      {err && <p className="err">{err}</p>}
    </Shell>
  )
}
